import type { StockWorklistFilter, StockWorklistScope } from '@dukanti/shared/admin';

export interface StockWorklistContext {
  readonly scope: StockWorklistScope;
  readonly filter: StockWorklistFilter;
  readonly query: string;
  readonly invalidQuery: boolean;
}

export function parseStockWorklistParams(params: URLSearchParams): StockWorklistContext {
  const scope = params.get('scope') === 'all' ? 'all' : 'active';
  const status = params.get('status');
  const filter = status === 'low_stock' || status === 'out_of_stock' ? status : 'all';
  const query = (params.get('q') ?? '').trim();
  return { scope, filter, query, invalidQuery: [...query].length > 100 };
}

export function buildStockWorklistPath(
  context: Pick<StockWorklistContext, 'scope' | 'filter' | 'query'>,
): string {
  const params = new URLSearchParams();
  if (context.scope !== 'active') params.set('scope', context.scope);
  if (context.filter !== 'all') params.set('status', context.filter);
  if (context.query.trim()) params.set('q', context.query.trim());
  return `/stock${params.size ? `?${params.toString()}` : ''}`;
}

/** Router state is untrusted. Only an exact, validated worklist destination is allowed. */
export function stockReturnPath(state: unknown): string | null {
  if (
    typeof state !== 'object' ||
    state === null ||
    !('stockReturnTo' in state) ||
    typeof state.stockReturnTo !== 'string'
  )
    return null;
  const value = state.stockReturnTo;
  if (value.length > 1600 || !/^\/stock(?:\?[^#]*)?$/.test(value)) return null;
  const params = new URLSearchParams(value.split('?')[1]);
  if (
    [...params.keys()].some(
      (key) => !['scope', 'status', 'q'].includes(key) || params.getAll(key).length > 1,
    )
  )
    return null;
  if (params.has('scope') && !['active', 'all'].includes(params.get('scope') ?? '')) return null;
  if (
    params.has('status') &&
    !['all', 'out_of_stock', 'low_stock'].includes(params.get('status') ?? '')
  )
    return null;
  const context = parseStockWorklistParams(params);
  return context.invalidQuery ? null : buildStockWorklistPath(context);
}

interface StockReturnAnchor {
  readonly variantId: string;
  readonly scrollY: number;
  readonly savedAt: number;
}
const anchors = new Map<string, StockReturnAnchor>();
let anchorIdentity: string | null = null;
const ANCHOR_TTL = 5 * 60_000;

export function setStockAnchorIdentity(storeId: string | null, userId: string | null): void {
  const identity = storeId && userId ? JSON.stringify([storeId, userId]) : null;
  if (identity !== anchorIdentity) {
    anchors.clear();
    anchorIdentity = identity;
  }
}

export function saveStockReturnAnchor(
  context: string,
  variantId: string,
  scrollY: number,
  now = Date.now(),
): void {
  if (!anchorIdentity || !Number.isFinite(scrollY) || scrollY < 0) return;
  anchors.delete(context);
  anchors.set(context, { variantId, scrollY, savedAt: now });
  for (const [key, anchor] of anchors) if (now - anchor.savedAt >= ANCHOR_TTL) anchors.delete(key);
  while (anchors.size > 16) {
    const oldest = anchors.keys().next().value;
    if (oldest === undefined) break;
    anchors.delete(oldest);
  }
}

/** Retain the current review position; hidden desktop/mobile duplicates are ignored. */
export function saveStockReviewAnchor(
  context: string,
  elements: Iterable<Element>,
  scrollY: number,
  now = Date.now(),
): void {
  let variantId: string | null = null;
  for (const element of elements) {
    const id = element.getAttribute('data-variant-id');
    const bounds = element.getBoundingClientRect();
    if (!id || bounds.width <= 0 || bounds.height <= 0) continue;
    variantId = id;
    if (bounds.bottom > 0) break;
  }
  // Below the list, the last rendered variant still anchors its footer position.
  if (variantId) saveStockReturnAnchor(context, variantId, scrollY, now);
}

export function readStockReturnAnchor(context: string, now = Date.now()): StockReturnAnchor | null {
  const anchor = anchors.get(context);
  if (!anchor) return null;
  if (now - anchor.savedAt >= ANCHOR_TTL) {
    anchors.delete(context);
    return null;
  }
  return anchor;
}
